import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Paper, Typography, TextField, Button, IconButton, useMediaQuery, useTheme, Grid, Divider, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import DropdownProductCode from './DropdownProductCode';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import EditIcon from '@mui/icons-material/Edit';
import Dropdown from '../../../components/Dropdown';
import { useVisibleColumns } from '../../../hooks/useVisibleColumns';
import { useUserDefine } from '../../../context/UserDefineContext';
import { COL, DEFAULT_LABELS } from '../../../constants/columnConstants';
import { useStockValidation } from '../../../hooks/useStockValidation';

// "Apply in Amt = Pkg*Qty*Rate?" — when this configure is enabled, a Pkg field
// containing anything that isn't a clean number (letters/special characters) must
// make the Pkg*Qty*Rate calculation come out as 0, instead of calculateAmount()'s
// own internal `Number(pkg) || 1` silently promoting invalid input back to 1.
// Blank/null/undefined Pkg is untouched — it still defaults to 1 (existing
// behavior for a fresh row before the user has typed a Pkg value).
const isGarbagePkg = (rawPkg) =>
    rawPkg !== '' && rawPkg !== null && rawPkg !== undefined && Number.isNaN(Number(rawPkg));

const getAmountCalcSafe = (qty, rate, rawPkg, disc, isApplyPkgInAmtEnabled, calculateAmount) => {
    if (isApplyPkgInAmtEnabled && isGarbagePkg(rawPkg)) {
        return { gross: 0, taxable: 0 };
    }
    return calculateAmount(qty, rate, rawPkg, disc);
};

const ProductDetails = ({ initialData, onNext, clientData, challanIds, connectionString, onRowsChange, mode, L_No }) => {

    const { user, selectedCompany, loading, configureData } = useAuth();
    console.log('📋 CreditNote ProductDetails: configureData from useAuth:', configureData?.length || 0, 'items');
    const { isColVisible, getColLabel } = useVisibleColumns('CREDIT');
    const { getSelectedCharges, getChargeItemId, userDefineData } = useUserDefine();
    // getSelectedCharges() returns a brand-new array on every call (Array.filter),
    // even when userDefineData hasn't changed — used as a useEffect dependency
    // further down, an unstable reference there re-fires those effects (and their
    // setState calls) on every render, causing an infinite render loop. Memoize
    // on userDefineData (the actual stable source data) so the array reference
    // only changes when the underlying charges data actually changes.
    const additionalCharges = useMemo(() => getSelectedCharges(), [userDefineData]);
    const { validateStock, isLastPurchaseRateEnabled, getLastPurchaseRate, isApplyMRPInSaleEnabled, calculateSaleRateFromMRP, isApplyPkgInAmtEnabled, calculateAmount, isApplyRateOnConversionEnabled, calculateConversion } = useStockValidation(connectionString, selectedCompany?.CompId);
    console.log('💾 CreditNote: isLastPurchaseRateEnabled:', isLastPurchaseRateEnabled);
    const [chargesDialogOpen, setChargesDialogOpen] = useState(false);
    const [chargesCollapsibleOpen, setChargesCollapsibleOpen] = useState(true);
    const [chargeInputs, setChargeInputs] = useState({});
    // Additional Charges contribution applied to the summary panel — populated
    // only when the user clicks Save in the Charges popup (see handleSaveCharges).
    const [chargesSummary, setChargesSummary] = useState({ total: 0, cgst: 0, sgst: 0, igst: 0 });

    // Restore chargeInputs and chargesSummary from initialData if provided (when loading an existing quotation)
    useEffect(() => {
        // API chargeInputs are already keyed by TransType (200, 201, 202, 203, 204)
        // which matches charge.TransType in additionalCharges, so no mapping needed
        if (initialData?.chargeInputs && Object.keys(initialData.chargeInputs).length > 0) {
            console.log('[ChargeInputs Debug] API chargeInputs keys:', Object.keys(initialData.chargeInputs));
            if (additionalCharges && additionalCharges.length > 0) {
                console.log('[ChargeInputs Debug] additionalCharges:', additionalCharges.map(c => ({ TransType: c.TransType, SrNo: c.SrNo, name: c.ChargeName })));
            }
            setChargeInputs(initialData.chargeInputs);
        }

        // Restore chargesSummary from charge rows in tableData (has actual calculated tax amounts)
        // This is more reliable since the API calculates taxes based on actual requirements
        if (initialData?.tableData) {
            const chargesTaxData = { total: 0, cgst: 0, sgst: 0, igst: 0 };

            initialData.tableData.forEach(row => {
                const transType = row.TransType != null ? parseInt(row.TransType) : 100;
                const isCharge = transType >= 200 && transType <= 204;
                if (isCharge) {
                    // Use the amount from the charge row
                    chargesTaxData.total += Number(row.amount || 0);
                    // Add the tax amounts from the charge row (already calculated by backend)
                    chargesTaxData.cgst += Number(row.CGSTAmt || 0);
                    chargesTaxData.sgst += Number(row.SGSTAmt || 0);
                    chargesTaxData.igst += Number(row.IGSTAmt || 0);
                }
            });

            console.log('[ChargesSummary Debug] Calculated from tableData:', chargesTaxData);

            if (chargesTaxData.total > 0 || chargesTaxData.cgst > 0 || chargesTaxData.sgst > 0 || chargesTaxData.igst > 0) {
                setChargesSummary(chargesTaxData);
            }
        }
        // Depend on the specific fields read above, not the whole initialData object —
        // initialData is a fresh object literal from the parent on every render
        // (formData.product || {}), so depending on it directly re-fires this effect
        // (and its setChargesSummary call) on every parent re-render, not just when
        // the actual saved charge data changes.
    }, [initialData?.chargeInputs, initialData?.tableData]);

    // Populate each charge slot's editable state (Amount, and Tax when TaxEnabled)
    // fresh every time the popup opens — default Tax is whatever was saved for
    // that slot in UserDefineColumns (charge.TaxId). Preserve amount if already restored from initialData.
    useEffect(() => {
        if (!chargesDialogOpen) return;
        setChargeInputs(prev => {
            const next = { ...prev };
            additionalCharges.forEach(charge => {
                const key = charge.TransType;
                if (!next[key]) {
                    // Only set empty amount if not already populated (e.g., from initialData restore)
                    next[key] = {
                        amount: '',
                        taxId: charge.TaxId ?? null,
                    };
                } else {
                    // Preserve existing amount, but update taxId to the configured one if not already set
                    if (next[key].taxId === null && charge.TaxId !== null) {
                        next[key].taxId = charge.TaxId;
                    }
                }
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chargesDialogOpen, additionalCharges]);

    const updateChargeInput = (key, patch) => {
        setChargeInputs(prev => ({
            ...prev,
            [key]: { ...prev[key], ...patch },
        }));
    };
    const state = clientData?.details?.['State'] || '';
    const compState = selectedCompany?.State || '';
    const companyId = selectedCompany?.CompId || null;
    // When this Credit Note was raised against a Sale Invoice (SIID present),
    // the product list must mirror that invoice exactly — editing is locked.
    const isProductEditLocked = clientData?.SIID !== undefined && clientData?.SIID !== null && String(clientData.SIID).trim() !== '';
    console.log('challanIds:', challanIds);
    console.log(`state: ${state} || compState: ${compState}`);
    console.log(initialData)
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [rows, setRows] = useState([]);
    const [nextSrNo, setNextSrNo] = useState(1);
    const [current, setCurrent] = useState({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, MRP: '', batchNo: '', expDate: '', serialNo: '', amount: '', taxAmount: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', UTGSTAmt: null, GSTCess: '0.00', GSTCEssAmt: null, GSTOth: '0.00', GSTEduCess: '0.00', GSTEducessAmt: null, CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', TaxId: null, challanDtsID: null, challanID: null, soId: null, soDtsID: null, POID: null, PODtsID: null, PONo: '', PODate: '', POQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth1Amt: '', oth2: '', oth2Amt: '', oth3: '', oth3Amt: '', oth4: '', oth4Amt: '', oth5: '', oth5Amt: '', oth6: '', oth6Amt: '', oth7: '', oth7Amt: '', Pf: '', PfAmt: '', Insu: '', InsuAmt: '', Freight: '', FreightAmt: '', DiscAmt: '', SOQty: '', TCS: '0.00', TCSAmt: null, TDS: '0.00', TDSAmt: null, ex4: '', ex5: '', exQty6: '', serialNo: '',
        // "Enable Convesrion effect in Transaction?" fields — defaultUnitId/convUnitId/productConvQty
        // are captured from productmaster at select time and stay the same.
        // baseRate preserves the rate as it stood before any conversion division,
        // so switching Unit back to defaultUnitId can restore it exactly.
        // ConversionQty/ConversionRate are only non-null while a conversion is
        // actively applied to this row (see purcinvoicedetails.ConversionQty/
        // ConversionRate columns) — both map 1:1 onto payload/stock fields.
        defaultUnitId: '', convUnitId: '', productConvQty: '', baseRate: 0, ConversionQty: null, ConversionRate: null });
    const [totals, setTotals] = useState({ basic: 0, discount: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, total: 0 });
    const [errors, setErrors] = useState({});
    const [editIndex, setEditIndex] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [colorMap, setColorMap] = useState({});
    const [sizeMap, setSizeMap] = useState({});

    const [isInitialized, setIsInitialized] = useState(false);

    const [gsts, setGsts] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [adjustAdvancePayment, setAdjustAdvancePayment] = useState(0);
    const [stockWarningOpen, setStockWarningOpen] = useState(false);
    const [stockWarningData, setStockWarningData] = useState(null);
    const [stockConfirmResolve, setStockConfirmResolve] = useState(null);
    const [adjustAmt, setAdjustAmt] = useState(0);
    const [isRecordFromReference, setIsRecordFromReference] = useState(false);

    // "Enable Convesrion effect in Transaction?" — Conversion Qty popup state.
    const [conversionQtyDialogOpen, setConversionQtyDialogOpen] = useState(false);
    const [conversionQtyInput, setConversionQtyInput] = useState('');
    const [pendingUnitChange, setPendingUnitChange] = useState(null); // { UnitId, unit, previousUnitId }

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };

    const API_BASE = `${connectionString}/api`;

    const transformGSTs = data =>
        Array.isArray(data)
            ? data.map(i => ({ Id: i.TaxID, value: i.Taxcode }))
            : [];

    const Hlp_Fn_GST = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/product-get-data/api/gst?companyId=${companyId}`);
            console.log(data)
            setGsts(transformGSTs(data));
        } catch (e) {
            console.error('GST lookup failed', e);
            setSnackbar({ open: true, message: 'GST lookup failed.', severity: 'error' });
            setGsts([]);
        }
    };


    // Track if component has finished initializing
    const hasInitializedRef = useRef(false);

    // When true, the next run of the live-calculation effect is skipped —
    // set right before a row's DB values are loaded into `current` (editRow),
    // so entering edit mode doesn't immediately overwrite the stored amount.
    const skipNextCalcRef = useRef(false);

    // Ref to the Product Code field for auto-focus
    const productCodeRef = useRef(null);

    // Notify parent when rows change â€” but ONLY after initialization is done
    useEffect(() => {
        if (!hasInitializedRef.current) return; // Don't fire on first empty render
        if (onRowsChange) {
            onRowsChange(rows);
        }
    }, [rows]);

    // Reset isInitialized when coming back with data but rows are empty
    useEffect(() => {
        if (initialData?.tableData?.length && rows.length === 0 && hasInitializedRef.current) {
            setIsInitialized(false);
        }
    }, [initialData?.tableData]);

    // For add mode â€” no initialData, so mark as initialized immediately
    useEffect(() => {
        if (!initialData?.tableData?.length) {
            hasInitializedRef.current = true;
        }
    }, []);


    useEffect(() => {
        Hlp_Fn_GST();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`, { params: { companyId } });
                setUnitOptions(unitsRes.data); // Expecting [{ UnitId: 43, unit: "NOS" }, ...]
                const [cRes, sRes] = await Promise.all([
                    axios.get(`${connectionString}/api/colormaster`),
                    axios.get(`${connectionString}/api/sizemaster`)
                ]);
                const cm = {}, sm = {};
                cRes.data.forEach(c => (cm[c.colorId] = { name: c.itemColor, code: c.colorCode }));
                sRes.data.forEach(s => (sm[s.sizeId] = { name: s.itemSize, code: s.sizeCode }));
                setColorMap(cm);
                setSizeMap(sm);
            } catch (error) {
                console.error('Error fetching product or unit data:', error);
            }
        };

        fetchData();
        loadAllProducts();
    }, []);

    const handleProductAdded = async () => {
        // Reload units when product is added
        try {
            const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`, {
                params: { companyId }
            });
            setUnitOptions(unitsRes.data);

            // Also reload products
            await loadAllProducts();
        } catch (error) {
            console.error('Error refreshing data:', error);
        }
    };

    const loadAllProducts = async () => {
        try {
            const res = await axios.get(`${connectionString}/api/productdata/retrive`, { params: { companyId, mode: 'SALE' } });
            setAllProducts(res.data);
        } catch (e) {
            console.error('Error fetching products:', e);
        }
    };

    const getTaxPercent = (value) => {
        if (!value) return 0;

        // remove % sign
        const cleaned = value.replace('%', '').trim();

        const parsed = parseFloat(cleaned);

        return isNaN(parsed) ? 0 : parsed;
    };

    useEffect(() => {
        if (isInitialized || !initialData?.tableData || !Array.isArray(initialData.tableData) || !unitOptions.length || !allProducts.length) {
            return;
        }

        // The products grid (`rows`) must only ever hold normal product rows.
        // Additional Charges (TransType 200-204) and RoundOff (300) are restored
        // separately (chargeInputs / roundOff field) and re-added at submit time —
        // including them here as well would duplicate them in the outgoing payload.
        const regularRows = initialData.tableData.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300);

        const initialRows = regularRows.map((r, index) => {
            const srNoValue = index + 1;
            const qty = Number(r.qty) || 0;
            const rate = Number(r.rate) || 0;
            const disc = Number(r.disc) || 0;
            // Prefer the exact amount stored in the database over recomputing it —
            // recomputing here can silently diverge from what was actually saved
            // (e.g. if a Pkg-based calculation produced the stored value). Only
            // fall back to qty*rate*(1-disc/100) when the DB has no stored amount.
            const storedAmt = r.TaxableAmt ?? r.amount;
            const hasStoredAmt = storedAmt != null && storedAmt !== '' && !Number.isNaN(Number(storedAmt));
            const taxableAmt = hasStoredAmt ? Number(storedAmt) : qty * rate * (1 - disc / 100);

            const product = allProducts.find(p => p.Id === r.productId || p.itemCode === r.productCode);

            let cgst = '0.00', sgst = '0.00', igst = '0.00';
            let cgstAmt = '0.00', sgstAmt = '0.00', igstAmt = '0.00';
            let taxId = r.taxId || r.TaxId || null;


            //  GST lookup
            const matchedGST = gsts.find(g => String(g.Id) === String(taxId));

            //  final %
            let tax = matchedGST
                ? getTaxPercent(matchedGST.value)
                : Number(r.tax) || (Number(r.CGST) + Number(r.SGST)) || Number(r.IGST) || 0;

            let taxAmount = taxableAmt * (tax / 100);
            const matchedUnit =
                unitOptions.find(u => String(u.UnitId) === String(r.UnitId)) ||
                unitOptions.find(u => u.unit?.toLowerCase() === String(r.unit).toLowerCase()) ||
                (product ? unitOptions.find(u =>
                    String(u.UnitId) === String(product.UnitId) ||
                    u.unit?.toLowerCase() === String(product.Unit?.toLowerCase())
                ) : null);

            if (product) {
                tax = tax || 0;       //  backend value
                taxId = r.taxId || r.TaxId || null;        //  backend value
                taxAmount = taxableAmt * (tax / 100);
                cgst = state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00';
                sgst = state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00';
                igst = state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00';
                cgstAmt = state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00';
                sgstAmt = state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00';
                igstAmt = state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00';
            } else {
                taxAmount = taxableAmt * (tax / 100);
                cgst = (Number(r.CGST) || (state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2) : 0)).toFixed(2);
                sgst = (Number(r.SGST) || (state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2) : 0)).toFixed(2);
                igst = (Number(r.IGST) || (state?.toLowerCase() !== compState?.toLowerCase() ? tax : 0)).toFixed(2);
                cgstAmt = (Number(r.CGSTAmt) || (state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2) : 0)).toFixed(2);
                sgstAmt = (Number(r.SGSTAmt) || (state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2) : 0)).toFixed(2);
                igstAmt = (Number(r.IGSTAmt) || (state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount : 0)).toFixed(2);
            }

            // "Enable Convesrion effect in Transaction?" — re-derive defaultUnitId/convUnitId/
            // productConvQty below from the product's CURRENT registered
            // defaults. Uses its own String(...)-normalized lookup rather than
            // the `product` above, since that one's `p.Id === r.productId`
            // compares a number to a string and never matches — this fix must
            // not depend on it (kept separate so the existing `product` and its
            // other uses above are left completely untouched).
            const productForConversion = allProducts.find(ap => String(ap.Id) === String(r.productId) || ap.itemCode === r.productCode);

            return {
                ...r,
                productId: r.productId || r.Id || r.id || '',
                UnitId: matchedUnit ? String(matchedUnit.UnitId) : (product?.UnitId || ''),
                unit: matchedUnit ? matchedUnit.unit : (product?.Unit || ''),
                qty,
                rate,
                disc,
                tax,
                TaxableAmt: taxableAmt.toFixed(2),
                taxAmount: Number(r.taxAmount) || taxAmount.toFixed(2),
                amount: taxableAmt.toFixed(2),
                CGST: cgst,
                SGST: sgst,
                IGST: igst,
                CGSTAmt: cgstAmt,
                SGSTAmt: sgstAmt,
                IGSTAmt: igstAmt,
                UTGST: String(r.UTGST || '0.00'),
                GSTCess: String(r.GSTCess || '0.00'),
                TaxId: taxId,
                srNo: srNoValue,
                challanDtsID: r.challanDtsID || null,
                challanID: r.challanID || null,
                soId: r.soId || null,
                soDtsID: r.soDtsID || null,
                // "Enable Convesrion effect in Transaction?" — re-derive these UI-only fields from
                // the product's CURRENT registered defaults (same as handleSelect),
                // since they're never persisted and a saved row's editRow spread
                // would otherwise leave defaultUnitId unset, silently disabling the
                // Unit-change conversion prompt in edit mode. baseRate stays the
                // ORIGINAL pre-conversion rate: r.ConversionRate when a conversion
                // was active on this saved row, else the row's own rate.
                defaultUnitId: matchedUnit ? String(matchedUnit.UnitId) : (productForConversion?.UnitId ? String(productForConversion.UnitId) : ''),
                convUnitId: productForConversion?.ConvUnit ? String(productForConversion.ConvUnit) : '',
                productConvQty: Number(productForConversion?.ConvQty) || '',
                baseRate: r.ConversionRate != null ? Number(r.ConversionRate) : rate,
                POID: r.POID || null,
                PODtsID: r.PODtsID || null,
                PONo: r.PONo || null,
                PODate: r.PODate || null,
                POQty: r.POQty || null,
                ex4: r.ex4 || null,
                ex5: r.ex5 || null,
                exQty6: r.exQty6 || null,
                serialNo: r.serialNo || null,
                oth1: r.oth1 || null,
                oth1Amt: r.oth1Amt || null,
                oth2: r.oth2 || null,
                oth2Amt: r.oth2Amt || null,
                oth3: r.oth3 || null,
                oth3Amt: r.oth3Amt || null,
                oth4: r.oth4 || null,
                oth4Amt: r.oth4Amt || null,
                oth5: r.oth5 || null,
                oth5Amt: r.oth5Amt || null,
                oth6: r.oth6 || null,
                oth6Amt: r.oth6Amt || null,
                oth7: r.oth7 || null,
                oth7Amt: r.oth7Amt || null,
                Pf: r.Pf || null,
                PfAmt: r.PfAmt || null,
                Insu: r.Insu || null,
                InsuAmt: r.InsuAmt || null,
                Freight: r.Freight || null,
                FreightAmt: r.FreightAmt || null,
                DiscAmt: r.DiscAmt || null,
                SOQty: r.SOQty || null,
                GSTOth: r.GSTOth || null,
                GSTCEssAmt: r.GSTCEssAmt || null,
                GSTEducessAmt: r.GSTEducessAmt || null,
                TCS: r.TCS || null,
                TCSAmt: r.TCSAmt || null,
                TDS: r.TDS || null,
                TDSAmt: r.TDSAmt || null,
                UTGSTAmt: r.UTGSTAmt || null};
        });
        console.log(initialRows);

        setRows(initialRows);
        setNextSrNo(initialRows.length + 1);
        setIsInitialized(true);
        hasInitializedRef.current = true; // â† for lifting rows state up 
    }, [initialData?.tableData, unitOptions, allProducts, state, compState, isInitialized]);

    useEffect(() => {
        // `current` was just loaded from a saved row (editRow) — skip this one
        // pass so the DB-stored amount isn't immediately overwritten before the
        // user has actually changed anything.
        if (skipNextCalcRef.current) {
            skipNextCalcRef.current = false;
            return;
        }

        const qty = Number(current.qty) || 0;
        const rate = Number(current.rate) || 0;
        const disc = Number(current.disc) || 0;
        const tax = Number(current.tax) || 0;
        const taxableAmt = getAmountCalcSafe(qty, rate, current.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent(prev => ({
            ...prev,
            TaxableAmt: taxableAmt.toFixed(2),
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00'
        }));
    }, [current.qty, current.rate, current.pkg, current.disc, current.tax, state, compState, isApplyPkgInAmtEnabled, calculateAmount]);

    // Recalculate Rate from MRP when GST (TaxId) changes, if Apply MRP in Sale is enabled
    useEffect(() => {
        if (!isApplyMRPInSaleEnabled || !current.MRP || Number(current.MRP) <= 0) return;

        const matchedGST = gsts.find(g => String(g.Id) === String(current.TaxId));
        if (!matchedGST) return;

        const tax = getTaxPercent(matchedGST.value);
        const newRate = calculateSaleRateFromMRP(Number(current.MRP), tax);
        const taxableAmt = current.qty * newRate * (1 - current.disc / 100);
        const taxAmount = taxableAmt * (tax / 100);

        console.log('🔄 CreditNote: GST changed, recalculating Rate from MRP:', { MRP: current.MRP, tax, newRate });

        setCurrent(prev => ({
            ...prev,
            rate: newRate,
            tax,
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            TaxableAmt: taxableAmt.toFixed(2)
        }));
    }, [current.TaxId, isApplyMRPInSaleEnabled, gsts]);

    useEffect(() => {
        const n = v => Number(v || 0);
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();

        let basicAmountSum = 0;
        let discountTotal = 0;
        let cgstTotal = 0;
        let sgstTotal = 0;
        let igstTotal = 0;

        rows.forEach(r => {
            const qty = n(r.qty);
            const rate = n(r.rate);
            const gross = qty * rate;
            const taxable = n(r.amount || r.TaxableAmt);
            const discAmtRow = n(r.DiscAmt) || Math.max(0, gross - taxable);
            const taxPct = n(r.tax);

            const cgstAmtRow = (r.CGSTAmt != null && r.CGSTAmt !== '') ? n(r.CGSTAmt) : (isSameState ? taxable * (taxPct / 100) / 2 : 0);
            const sgstAmtRow = (r.SGSTAmt != null && r.SGSTAmt !== '') ? n(r.SGSTAmt) : (isSameState ? taxable * (taxPct / 100) / 2 : 0);
            const igstAmtRow = (r.IGSTAmt != null && r.IGSTAmt !== '') ? n(r.IGSTAmt) : (isSameState ? 0 : taxable * (taxPct / 100));

            basicAmountSum += taxable;
            discountTotal += discAmtRow;
            cgstTotal += cgstAmtRow;
            sgstTotal += sgstAmtRow;
            igstTotal += igstAmtRow;
        });

        const totalTax = cgstTotal + sgstTotal + igstTotal;
        // Fold Additional Charges (amount + their own GST) into the pre-rounding
        // amount so Round Off reflects the actual payable total, not just products.
        const chargesTotal = Number(chargesSummary.total || 0)
            + Number(chargesSummary.cgst || 0)
            + Number(chargesSummary.sgst || 0)
            + Number(chargesSummary.igst || 0);
        const finalAmount = basicAmountSum + totalTax + chargesTotal;

        // const roundedFinal = Math.ceil(finalAmount);
        // const roundOff = Number((roundedFinal - finalAmount).toFixed(2));
        const decimalPart = finalAmount - Math.floor(finalAmount);

        const roundedFinal =
            decimalPart >= 0.5
                ? Math.ceil(finalAmount)
                : Math.floor(finalAmount);

        const roundOff = Number((roundedFinal - finalAmount).toFixed(2));

        setTotals({
            basic: basicAmountSum.toFixed(2),
            discount: discountTotal.toFixed(2),
            cgst: cgstTotal.toFixed(2),
            sgst: sgstTotal.toFixed(2),
            igst: igstTotal.toFixed(2),
            roundOff: roundOff.toFixed(2),
            total: roundedFinal.toFixed(2)
        });
    }, [rows, state, compState, chargesSummary]);

    // Additional Charges: given a charge and its current popup input, compute
    // the charge amount, the tax on it (only when TaxEnabled), and the final
    // (tax-inclusive) amount — this is what "Final Amount" displays live.
    const getChargeFinalAmounts = (charge, input) => {
        const chargeAmount = Number(input?.amount) || 0;
        if (!charge.TaxEnabled) {
            return { chargeAmount, taxPercent: 0, taxAmount: 0, finalAmount: chargeAmount };
        }
        const selectedGST = gsts.find(g => String(g.Id) === String(input?.taxId));
        const taxPercent = parseFloat(selectedGST?.value) || 0;
        const taxAmount = chargeAmount * taxPercent / 100;
        return { chargeAmount, taxPercent, taxAmount, finalAmount: chargeAmount + taxAmount };
    };

    // Additional Charges: on Save, fold every charge's final amount into the
    // Total Amount shown in the summary, and its tax (if any) into CGST+SGST
    // (same state) or IGST (different state) — same split rule the totals
    // useEffect above already uses for product rows.
    const handleSaveCharges = () => {
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();
        let addTotal = 0, addCgst = 0, addSgst = 0, addIgst = 0;
        additionalCharges.forEach(charge => {
            const key = charge.TransType;
            const input = chargeInputs[key];
            if (!input || !input.amount) return;
            const amount = Number(input.amount) || 0;
            const { taxAmount } = getChargeFinalAmounts(charge, input);
            addTotal += amount;
            if (charge.TaxEnabled) {
                if (isSameState) {
                    addCgst += taxAmount / 2;
                    addSgst += taxAmount / 2;
                } else {
                    addIgst += taxAmount;
                }
            }
        });
        setChargesSummary({ total: addTotal, cgst: addCgst, sgst: addSgst, igst: addIgst });
        setChargesDialogOpen(false);
    };

    // Auto-sync chargesSummary with chargeInputs whenever charges are entered
    // This ensures charges are calculated in real-time without needing a Save button
    useEffect(() => {
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();
        let addTotal = 0, addCgst = 0, addSgst = 0, addIgst = 0;
        additionalCharges.forEach(charge => {
            const key = charge.TransType;
            const input = chargeInputs[key];
            if (!input || !input.amount) return;
            const amount = Number(input.amount) || 0;
            const { taxAmount } = getChargeFinalAmounts(charge, input);
            addTotal += amount;
            if (charge.TaxEnabled) {
                if (isSameState) {
                    addCgst += taxAmount / 2;
                    addSgst += taxAmount / 2;
                } else {
                    addIgst += taxAmount;
                }
            }
        });
        setChargesSummary({ total: addTotal, cgst: addCgst, sgst: addSgst, igst: addIgst });
    }, [chargeInputs, additionalCharges, state, compState, gsts]);

    const handleChange = field => e => {
        if (field === 'UnitId') {
            const selectedUnit = unitOptions.find(u => u.UnitId === Number(e.target.value));
            const newUnitId = e.target.value;

            // "Enable Convesrion effect in Transaction?" — only intervene when the config is on
            // and the newly-picked unit differs from this product's own default
            // Unit (current.defaultUnitId, captured at select time). Changing
            // back to the default reverts immediately with no popup (see below).
            if (isApplyRateOnConversionEnabled && current.defaultUnitId && String(newUnitId) !== String(current.defaultUnitId)) {
                setPendingUnitChange({
                    UnitId: newUnitId,
                    unit: selectedUnit ? selectedUnit.unit : '',
                    previousUnitId: current.UnitId,
                });
                // Pre-fill per spec: the product's registered ConvQty only when the
                // newly-picked unit matches the product's registered ConvUnit;
                // otherwise default to 1 (no known ratio for an unrelated unit).
                const matchesConvUnit = current.convUnitId && String(newUnitId) === String(current.convUnitId);
                setConversionQtyInput(matchesConvUnit && current.productConvQty ? String(current.productConvQty) : '1');
                setConversionQtyDialogOpen(true);
                return;
            }

            // Reverting to the product's own default Unit (or config is off) —
            // behave exactly as before, and clear any previously-active conversion.
            setCurrent(prev => ({
                ...prev,
                UnitId: newUnitId,
                unit: selectedUnit ? selectedUnit.unit : '',
                ConversionQty: null,
                ConversionRate: null,
                rate: prev.defaultUnitId && String(newUnitId) === String(prev.defaultUnitId) ? prev.baseRate : prev.rate,
            }));
        } else {
            setCurrent(prev => ({ ...prev, [field]: e.target.value }));
        }
    };

    // "Enable Convesrion effect in Transaction?" — Confirm applies the pending Unit change plus
    // the Conversion Qty the user entered/accepted, deriving the billed Rate from
    // this row's baseRate (the rate as it stood before any conversion — captured
    // once at product-select time and preserved untouched so it can always be
    // recovered). Cancel reverts the Unit selection back to whatever it was
    // before, so a cancelled popup never leaves the row half-changed.
    const handleConfirmConversionQty = () => {
        if (!pendingUnitChange) return;
        const convQty = Number(conversionQtyInput) || 0;

        if (convQty > 0) {
            const { rate: convertedRate } = calculateConversion(current.baseRate, current.qty, convQty);
            setCurrent(prev => ({
                ...prev,
                UnitId: pendingUnitChange.UnitId,
                unit: pendingUnitChange.unit,
                rate: convertedRate,
                ConversionQty: convQty,
                ConversionRate: prev.baseRate,
            }));
        } else {
            // Invalid/blank conversion qty — treat like Cancel rather than divide by zero.
            setCurrent(prev => ({ ...prev, UnitId: pendingUnitChange.previousUnitId }));
        }

        setConversionQtyDialogOpen(false);
        setPendingUnitChange(null);
        setConversionQtyInput('');
    };

    const handleCancelConversionQty = () => {
        if (pendingUnitChange) {
            setCurrent(prev => ({ ...prev, UnitId: pendingUnitChange.previousUnitId }));
        }
        setConversionQtyDialogOpen(false);
        setPendingUnitChange(null);
        setConversionQtyInput('');
    };

    const handleSelect = async product => {
        console.log('🔍 Product selected - Full product object:', product);
        console.log('🔍 Product.MRP:', product.MRP);

        const matchedUnit = unitOptions.find(opt => {
            if (product.UnitId && opt.UnitId === product.UnitId) return true;
            if (product.Unit && opt.unit?.toLowerCase() === product.Unit.toLowerCase()) return true;
            if (product.unit && opt.unit?.toLowerCase() === product.unit.toLowerCase()) return true;
            return false;
        });
        const qty = 1;
        let rate = Number(product.Rate) || 0;
        let disc = Number(product.Discount) || 0;
        let MRP = Number(product.MRP) || '';
        console.log('💰 MRP value extracted:', MRP);

        // Fetch last purchase rate if enabled
        console.log('📦 CreditNote: Product selected, checking if last purchase rate should be fetched');
        if (isLastPurchaseRateEnabled) {
            console.log('💾 CreditNote: Fetching last purchase rate for product:', product.Id, 'party:', clientData?.clientId);
            const lastPurchaseData = await getLastPurchaseRate(product.Id, clientData?.clientId);
            if (lastPurchaseData.rate !== null) {
                rate = lastPurchaseData.rate;
                console.log('✅ CreditNote: Last purchase rate fetched:', rate);
            }
            if (lastPurchaseData.disc !== null) {
                disc = lastPurchaseData.disc;
                console.log('✅ CreditNote: Last purchase disc fetched:', disc);
            }
        }

        const tax = parseFloat(product.GST) || 0;
        const taxId = product.TaxId || null;
        const taxableAmt = qty * rate * (1 - disc / 100);
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent({
            Id: product.Id?.toString() || '',
            productCode: product.itemCode || '',
            description: product.description || '',
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : '',
            unit: matchedUnit ? matchedUnit.unit : '',
            qty,
            rate,
            disc,
            tax,
            MRP,
            batchNo: '',
            expDate: '',
            serialNo: '',
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            remark: '',
            TaxId: taxId,
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            UTGST: '0.00',
            GSTCess: '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            TaxableAmt: taxableAmt.toFixed(2),
            challanDtsID: null,
            challanID: null,
            soId: null,
            soDtsID: null,
            POID: null,
            PODtsID: null,
            PONo: '',
            PODate: '',
            POQty: '',
            type: '',
            size: '',
            color: '',
            freeQty: '',
            per: '',
            pkg: '',
            oth1: '',
            oth1Amt: '',
            oth2: '',
            oth2Amt: '',
            oth3: '',
            oth3Amt: '',
            oth4: '',
            oth4Amt: '',
            oth5: '',
            oth5Amt: '',
            oth6: '',
            oth6Amt: '',
            oth7: '',
            oth7Amt: '',
            Pf: '',
            PfAmt: '',
            Insu: '',
            InsuAmt: '',
            Freight: '',
            FreightAmt: '',
            DiscAmt: '',
            SOQty: '',
            GSTOth: '0.00',
            GSTCEssAmt: null,
            GSTEducessAmt: null,
            TCS: '0.00',
            TCSAmt: null,
            TDS: '0.00',
            TDSAmt: null,
            ex4: '',
            ex5: '',
            exQty6: '',
            serialNo: '',
            // "Enable Convesrion effect in Transaction?" — captured from productmaster at select
            // time; no conversion is active yet on a freshly-selected row.
            defaultUnitId: matchedUnit ? String(matchedUnit.UnitId) : '',
            convUnitId: product.ConvUnit ? String(product.ConvUnit) : '',
            productConvQty: Number(product.ConvQty) || '',
            baseRate: rate,
            ConversionQty: null,
            ConversionRate: null
        });
        // setEditIndex(null);
        setErrors({});
    };

    const addProduct = async () => {
        const errs = {};
        const messages = new Set();
        const requiredFields = ['productCode', 'description', 'UnitId', 'qty', 'rate', 'tax'];

        requiredFields.forEach(field => {
            if (!current[field] && current[field] !== 0) {
                errs[field] = 'Required';
                messages.add('All fields are required.');
            }
        });

        //GST validation
          if (!current.TaxId) {
            errs.TaxId = true;
            messages.add('Please select GST before adding product.');
        }


        if (!current.qty || Number(current.qty) <= 0) {
            errs.qty = 'Must be > 0';
            messages.add('Qty must be greater than 0');
        }

        if (!current.rate || Number(current.rate) <= 0) {
            errs.rate = 'Rate must be greater than 0';
            messages.add('Rate must be greater than 0');
        }

        if (current.disc < 0) {
            errs.disc = 'Cannot be negative';
            messages.add('Discount cannot be negative');
        }

        if (current.disc > 100) {
            errs.disc = 'Cannot be more than 100';
            messages.add('Discount cannot be more than 100');
        }

        if (messages.size > 0) {
            errs.global = Array.from(messages);
            setErrors(errs);
            return;
        }

        // Stock validation check
        const creditQty = Number(current.qty) || 0;
        const lNo = L_No || null;
        console.log('ProductDetails: Calling validateStock with itemId:', current.Id, 'qty:', creditQty, 'L_No:', lNo);
        const stockValidation = await validateStock(current.Id, creditQty, lNo);
        console.log('ProductDetails: Stock validation result:', stockValidation);

        if (stockValidation.shouldShowWarning) {
            console.log('ProductDetails: Showing warning dialog');
            const confirmed = await new Promise((resolve) => {
                setStockWarningData(stockValidation);
                setStockConfirmResolve(() => resolve);
                setStockWarningOpen(true);
            });
            if (!confirmed) {
                console.log('ProductDetails: User cancelled, not adding product');
                return;
            }
        } else {
            console.log('ProductDetails: No warning needed, proceeding with add');
        }

        const safeDisc =
            current.disc === '' || current.disc == null
                ? 0
                : Number(current.disc);

        const qty = Number(current.qty) || 0;
        const rate = Number(current.rate) || 0;
        // const disc = Number(current.disc) || 0;
        const disc = safeDisc || 0;
        const tax = Number(current.tax) || 0;
        const taxableAmt = getAmountCalcSafe(qty, rate, current.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        const taxAmount = taxableAmt * (tax / 100);

        // ✅ NEW: Check if in reference mode (mode === "ref")
        const isReferenceMode = mode === "ref";

        const row = {
            ...current,
            productId: current.Id,
            qty,
            rate,
            disc,
            tax,
            remark: current.remark || '',
            TaxableAmt: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            amount: taxableAmt.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            UTGST: current.UTGST,
            GSTCess: current.GSTCess,
            TaxId: current.TaxId,
            srNo: editIndex != null ? rows[editIndex].srNo : nextSrNo,
            // ✅ NEW: In reference mode, set challanID when adding first product to empty table
            challanDtsID: editIndex != null ? rows[editIndex].challanDtsID : (isReferenceMode ? null : null),
            challanID: editIndex != null ? rows[editIndex].challanID : (isReferenceMode ? Number(clientData.challanId) : null),
            soId: editIndex != null ? rows[editIndex].soId : null,
            soDtsID: editIndex != null ? rows[editIndex].soDtsID : null,
            POID: null,
            PODtsID: null
        };

        const existingRowIndex = rows.findIndex(
            (r, i) => r.productCode === current.productCode && i !== editIndex
        );

        // if (existingRowIndex !== -1 && editIndex == null) {
        //     setRows(prev =>
        //         prev.map((r, i) => {
        //             if (i === existingRowIndex) {
        //                 const newQty = Number(r.qty) + qty;
        //                 const newTaxableAmt = newQty * rate * (1 - disc / 100);
        //                 const newTaxAmount = newTaxableAmt * (tax / 100);
        //                 return {
        //                     ...r,
        //                     qty: newQty,
        //                     rate,
        //                     disc,
        //                     tax,
        //                     remark: current.remark || r.remark,
        //                     TaxableAmt: newTaxableAmt.toFixed(2),
        //                     taxAmount: newTaxAmount.toFixed(2),
        //                     amount: newTaxableAmt.toFixed(2),
        //                     CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
        //                     SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
        //                     IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
        //                     CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (newTaxAmount / 2).toFixed(2) : '0.00',
        //                     SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (newTaxAmount / 2).toFixed(2) : '0.00',
        //                     IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? newTaxAmount.toFixed(2) : '0.00',
        //                     UTGST: current.UTGST,
        //                     GSTCess: current.GSTCess,
        //                     TaxId: current.TaxId,
        //                     challanDtsID: r.challanDtsID,
        //                     challanID: r.challanID,
        //                     soId: r.soId,
        //                     soDtsID: r.soDtsID,
        //                     POID: r.POID || null,
        //                     PODtsID: r.PODtsID || null
        //                 };
        //             }
        //             return r;
        //         })
        //     );
        // } else {
        //     if (editIndex != null) {
        //         setRows(prev => prev.map((r, i) => (i === editIndex ? row : r)));
        //     } else {
        //         setRows(prev => [row, ...prev]);
        //         setNextSrNo(prev => prev + 1);
        //     }
        // }

        if (editIndex != null) {
            setRows(prev => prev.map((r, i) => i === editIndex ? { ...row, srNo: rows[editIndex].srNo } : r));
        } else {
            setRows(prev => [row, ...prev]);
            setNextSrNo(prev => prev + 1);
        }


        setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, MRP: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', UTGSTAmt: null, GSTCess: '0.00', GSTCEssAmt: null, GSTOth: '0.00', GSTEduCess: '0.00', GSTEducessAmt: null, CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null, challanDtsID: null, challanID: null, soId: null, soDtsID: null, POID: null, PODtsID: null, PONo: '', PODate: '', POQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth1Amt: '', oth2: '', oth2Amt: '', oth3: '', oth3Amt: '', oth4: '', oth4Amt: '', oth5: '', oth5Amt: '', oth6: '', oth6Amt: '', oth7: '', oth7Amt: '', Pf: '', PfAmt: '', Insu: '', InsuAmt: '', Freight: '', FreightAmt: '', DiscAmt: '', SOQty: '', TCS: '0.00', TCSAmt: null, TDS: '0.00', TDSAmt: null, ex4: '', ex5: '', exQty6: '', serialNo: '', defaultUnitId: '', convUnitId: '', productConvQty: '', baseRate: 0, ConversionQty: null, ConversionRate: null });
        setErrors({});
        setEditIndex(null);

        // Focus on Product Code field after adding product (not after updating)
        if (editIndex == null) {
            setTimeout(() => {
                productCodeRef.current?.focus?.();
            }, 0);
        }
    };

    const editRow = idx => {
        const row = rows[idx];
        const matchedUnit = unitOptions.find(u => String(u.UnitId) === String(row.UnitId)) ||
            unitOptions.find(u => String(u.UnitId) === String(row.unit)) ||
            unitOptions.find(u => u.unit?.toLowerCase() === String(row.unit).toLowerCase());

        // This row's Qty/Rate/Pkg/Disc are about to be loaded into `current` from
        // the DB values — skip the next live-calculation pass so it doesn't
        // immediately overwrite the stored amount before the user edits anything.
        skipNextCalcRef.current = true;

        setCurrent({
            ...row,
            Id: row.productId,
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : row.UnitId || '',
            unit: matchedUnit ? matchedUnit.unit : row.unit || '',
            CGST: String(row.CGST || '0.00'),
            SGST: String(row.SGST || '0.00'),
            IGST: String(row.IGST || '0.00'),
            CGSTAmt: String(row.CGSTAmt || '0.00'),
            SGSTAmt: String(row.SGSTAmt || '0.00'),
            IGSTAmt: String(row.IGSTAmt || '0.00'),
            UTGST: String(row.UTGST || '0.00'),
            GSTCess: String(row.GSTCess || '0.00'),
            TaxableAmt: String(row.TaxableAmt || '0.00'),
            taxAmount: String(row.taxAmount || '0.00'),
            amount: String(row.amount || '0.00'),
            TaxId: row.TaxId || null,
            challanDtsID: row.challanDtsID || null,
            challanID: row.challanID || null,
            soId: row.soId || null,
            soDtsID: row.soDtsID || null,
            POID: row.POID || null,
            PODtsID: row.PODtsID || null
        });
        setEditIndex(idx);
    };

    const deleteRow = idx => {
        setRows(prev => prev.filter((_, i) => i !== idx));
        if (idx === editIndex) {
            setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, MRP: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', UTGSTAmt: null, GSTCess: '0.00', GSTCEssAmt: null, GSTOth: '0.00', GSTEduCess: '0.00', GSTEducessAmt: null, CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null, challanDtsID: null, challanID: null, soId: null, soDtsID: null, POID: null, PODtsID: null, PONo: '', PODate: '', POQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth1Amt: '', oth2: '', oth2Amt: '', oth3: '', oth3Amt: '', oth4: '', oth4Amt: '', oth5: '', oth5Amt: '', oth6: '', oth6Amt: '', oth7: '', oth7Amt: '', Pf: '', PfAmt: '', Insu: '', InsuAmt: '', Freight: '', FreightAmt: '', DiscAmt: '', SOQty: '', TCS: '0.00', TCSAmt: null, TDS: '0.00', TDSAmt: null, ex4: '', ex5: '', exQty6: '', serialNo: '', defaultUnitId: '', convUnitId: '', productConvQty: '', baseRate: 0, ConversionQty: null, ConversionRate: null });
            setEditIndex(null);
            setErrors({});
        }
    };

    const handleNext = () => {
        if (rows.length === 0) {
            setErrors({ global: 'Add at least one product' });
            return;
        }

        const isSameState = state?.toLowerCase() === compState?.toLowerCase();

        const payloadRows = rows.map(r => {
            const qty = Number(r.qty) || 0;
            const rate = Number(r.rate) || 0;
            const gross = qty * rate;
            const taxable = Number(r.amount || r.TaxableAmt) || 0;
            const discAmt = Number(r.DiscAmt) || Math.max(0, gross - taxable);
            const taxPct = Number(r.tax) || 0;
            const taxAmt = Number(r.taxAmount) || (taxable * (taxPct / 100));

            const cgstAmt = (r.CGSTAmt != null && r.CGSTAmt !== '') ? Number(r.CGSTAmt) : (isSameState ? taxAmt / 2 : 0);
            const sgstAmt = (r.SGSTAmt != null && r.SGSTAmt !== '') ? Number(r.SGSTAmt) : (isSameState ? taxAmt / 2 : 0);
            const igstAmt = (r.IGSTAmt != null && r.IGSTAmt !== '') ? Number(r.IGSTAmt) : (isSameState ? 0 : taxAmt);

            return {
                ...r,
                productId: r.productId,
                productCode: r.productCode,
                description: r.description,
                unit: r.unit,
                UnitId: Number(r.UnitId) || null,
                qty,
                rate,
                // "Enable Convesrion effect in Transaction?" — ConversionQty/ConversionRate mirror
                // purcinvoicedetails' columns 1:1 (null on a normal, non-converted
                // row). stockQty is the base-unit-equivalent quantity to post to
                // stockinout's StockInQty/StockOutQty; it equals qty/ConversionQty
                // when a conversion is active on this row, otherwise the plain qty.
                ConversionQty: r.ConversionQty != null ? Number(r.ConversionQty) : null,
                ConversionRate: r.ConversionRate != null ? Number(r.ConversionRate) : null,
                stockQty: r.ConversionQty
                    ? Number(((Number(r.qty) || 0) / Number(r.ConversionQty)).toFixed(2))
                    : Number(r.qty) || 0,
                MRP: Number(r.MRP) || 0,
                disc: Number(r.disc) || 0,
                tax: taxPct,
                batchNo: r.batchNo || '',
                expDate: r.expDate || null,
                serialNo: r.serialNo || '',
                remark: r.remark || '',
                gross: Number(gross.toFixed(2)),
                amount: Number(taxable.toFixed(2)),
                DiscAmt: Number(discAmt.toFixed(2)),
                taxAmount: Number(taxAmt.toFixed(2)),
                srNo: r.srNo,
                TaxId: r.TaxId || null,
                CGST: isSameState ? Number((taxPct / 2).toFixed(2)) : 0,
                SGST: isSameState ? Number((taxPct / 2).toFixed(2)) : 0,
                IGST: isSameState ? 0 : Number(taxPct.toFixed(2)),
                CGSTAmt: Number(cgstAmt.toFixed(2)),
                SGSTAmt: Number(sgstAmt.toFixed(2)),
                IGSTAmt: Number(igstAmt.toFixed(2)),
                UTGST: r.UTGST || '0.00',
                GSTCess: r.GSTCess || '0.00',
                challanDtsID: r.challanDtsID || null,
                challanID: r.challanID || null,
                soId: r.soId || null,
                soDtsID: r.soDtsID || null,
                POID: r.POID || null,
                PODtsID: r.PODtsID || null,
                PONo: r.PONo || null,
                PODate: r.PODate || null,
                POQty: r.POQty || null,
                type: r.type || null,
                size: r.size || null,
                color: r.color || null,
                freeQty: r.freeQty || null,
                per: r.per || null,
                pkg: r.pkg || null,
                oth1: r.oth1 || null,
                oth1Amt: r.oth1Amt || null,
                oth2: r.oth2 || null,
                oth2Amt: r.oth2Amt || null,
                oth3: r.oth3 || null,
                oth3Amt: r.oth3Amt || null,
                oth4: r.oth4 || null,
                oth4Amt: r.oth4Amt || null,
                oth5: r.oth5 || null,
                oth5Amt: r.oth5Amt || null,
                oth6: r.oth6 || null,
                oth6Amt: r.oth6Amt || null,
                oth7: r.oth7 || null,
                oth7Amt: r.oth7Amt || null,
                Pf: r.Pf || null,
                PfAmt: r.PfAmt || null,
                Insu: r.Insu || null,
                InsuAmt: r.InsuAmt || null,
                Freight: r.Freight || null,
                FreightAmt: r.FreightAmt || null,
                DiscAmt: r.DiscAmt || null,
                SOQty: r.SOQty || null,
                GSTOth: r.GSTOth || null,
                GSTCEssAmt: r.GSTCEssAmt || null,
                GSTEducessAmt: r.GSTEducessAmt || null,
                TCS: r.TCS || null,
                TCSAmt: r.TCSAmt || null,
                TDS: r.TDS || null,
                TDSAmt: r.TDSAmt || null,
                UTGSTAmt: r.UTGSTAmt || null,
                ex4: r.ex4 || null,
                ex5: r.ex5 || null,
                exQty6: r.exQty6 || null,
                serialNo: r.serialNo || null
            };
        });

        // Additional Charges: charges saved in UserDefineColumns with ColToBeAsk=1
        // (i.e. present in `additionalCharges`, sourced from UserDefineContext) are
        // folded into the credit note payload — Adc{SrNo}Amt/Adc{SrNo}TaxId in summary
        // for all 5 fixed slots, plus one pseudo item row per SELECTED charge
        // (TransType 200-204, SrNo 20000-20004) appended to tableData.
        // When a charge's Tax is disabled, its TaxId is resolved to the taxmaster
        // row where GST = 0 (found via gsts, whose `value` doubles as the % — same
        // pattern already used elsewhere in this file, e.g. parseFloat(opt.value)).
        const zeroTaxId = gsts.find(g => parseFloat(g.value) === 0)?.Id ?? null;

        const additionalChargeSummary = {};
        const additionalChargeItems = [];
        for (let slot = 1; slot <= 5; slot++) {
            const charge = additionalCharges.find(c => Number(c.SrNo) === slot);
            if (!charge) {
                additionalChargeSummary[`Adc${slot}Amt`] = 0;
                additionalChargeSummary[`Adc${slot}TaxId`] = null;
                continue;
            }
            const key = charge.TransType;
            const input = chargeInputs[key] || { amount: '', taxId: charge.TaxId ?? null };
            const { taxPercent, taxAmount, finalAmount } = getChargeFinalAmounts(charge, input);
            const amount = Number(input.amount) || 0;
            const taxId = charge.TaxEnabled ? (input.taxId ?? null) : zeroTaxId;

            additionalChargeSummary[`Adc${slot}Amt`] = amount;
            additionalChargeSummary[`Adc${slot}TaxId`] = taxId;

            // The item row (creditnotedetails) is now shaped exactly like a normal
            // product row (same field set as payloadRows below) instead of a small
            // subset — CGST/SGST/IGST hold the % rate, CGSTAmt/SGSTAmt/IGSTAmt hold
            // the tax amounts, TaxableAmt holds the tax-inclusive final amount (e.g.
            // 100 + 18% tax = 118), and every other normal-row field gets a sensible
            // default/analog for a charge line (no productCode/qty/rate in the usual
            // sense, so ChargeName stands in for productCode/description, qty=1,
            // rate=amount so gross/amount line up the same way a real row's do).
            const chargeItemId = getChargeItemId( slot);
            additionalChargeItems.push({
                Id: chargeItemId,
                productId: chargeItemId,
                productCode: charge.ChargeName || '',
                description: charge.ChargeName || '',
                UnitId: null,
                unit: '',
                qty: 1,
                rate: amount,
                MRP: 0,
                disc: 0,
                tax: Number(taxPercent.toFixed(2)),
                batchNo: '',
                expDate: null,
                serialNo: '',
                amount: amount,
                taxAmount: Number(taxAmount.toFixed(2)),
                remark: '',
                TaxId: taxId,
                CGST: isSameState ? Number((taxPercent / 2).toFixed(2)) : 0,
                SGST: isSameState ? Number((taxPercent / 2).toFixed(2)) : 0,
                IGST: isSameState ? 0 : Number(taxPercent.toFixed(2)),
                UTGST: '0.00',
                GSTCess: '0.00',
                GSTOth: '0.00',
                GSTEduCess: '0.00',
                GSTEducessAmt: '0.00',
                CGSTAmt: isSameState ? Number((taxAmount / 2).toFixed(2)) : 0,
                SGSTAmt: isSameState ? Number((taxAmount / 2).toFixed(2)) : 0,
                IGSTAmt: isSameState ? 0 : Number(taxAmount.toFixed(2)),
                GSTCEssAmt: '0.00',
                TaxableAmt: Number(finalAmount.toFixed(2)),
                challanDtsID: null,
                challanID: null,
                soId: null,
                soDtsID: null,
                PONo: null,
                PODate: null,
                poQty: null,
                type: null,
                size: null,
                color: null,
                freeQty: null,
                per: null,
                pkg: null,
                oth1: null,
                oth2: null,
                oth3: null,
                oth4: null,
                oth5: null,
                oth6: null,
                oth7: null,
                ex4: null,
                ex5: null,
                exQty6: null,
                serialNo: '',
                configData: [],
                _isNew: true,
                configType: null,
                applyBatchNo: 0,
                applySerialNo: 0,
                warrantyMonths: null,
                expiryDays: null,
                srNo: 19999 + slot,
                dispQty: 0,
                gross: amount,
                DiscAmt: 0,
                refNo: null,
                refDate: null,
                refQty: null,
                oth1Amt: null,
                oth2Amt: null,
                oth3Amt: null,
                oth4Amt: null,
                oth5Amt: null,
                oth6Amt: null,
                oth7Amt: null,
                // Fixed identifiers driving the backend's TransType-aware SrNo
                // override (see dispatchController.js / creditNoteController.js).
                SrNo: 19999 + slot,
                TransType: 199 + slot,
            });
        }

        // ✅ NEW: Collect ChallanDtsIDs only from rows with product details
        const challanDtsIdsWithDetails = payloadRows
            .filter(row => row.challanDtsID && row.challanDtsID > 0)
            .map(row => row.challanDtsID);

        const payload = {
            tableData: [...payloadRows, ...additionalChargeItems],
            summary: {
                basic: Number(totals.basic),
                discount: Number(totals.discount),
                cgst: Number(totals.cgst) + Number(chargesSummary.cgst || 0),
                sgst: Number(totals.sgst) + Number(chargesSummary.sgst || 0),
                igst: Number(totals.igst) + Number(chargesSummary.igst || 0),
                roundOff: Number(totals.roundOff),
                total: Number(totals.total),
                adjustAdvancePayment,
                adjustAmt,
                ...additionalChargeSummary
            },
            chargeInputs: chargeInputs,
            // ✅ NEW: Include ChallanID from clientData for reference tracking (stored as SIID in purcinvoicemaster)
            challanId: clientData?.challanId ? Number(clientData.challanId) : null,
            siid: clientData?.challanId ? Number(clientData.challanId) : null,
            // ✅ NEW: Include ChallanDtsIDs only if detail data exists
            challanDtsIds: challanDtsIdsWithDetails.length > 0 ? challanDtsIdsWithDetails : []
        };

        onNext(payload);
    };

    const formatINR = val => {
        const num = Number(val) || 0;
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const columns = [
        { field: 'srNo', headerName: '#', width: 40, align: 'center', headerAlign: 'center' },
        {
            field: 'product', headerName: 'Product', flex: 1, sortable: false, filterable: false,
            renderCell: params => {
                const { productCode, description, unit, tax, batchNo, expDate, serialNo, remark, CGSTAmt, SGSTAmt, IGSTAmt, BillNumber, BillDate } = params.row;
                const isSameState = state?.toLowerCase() === compState?.toLowerCase();
                return (
                    <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column' }}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" component="div"
                                sx={isProductEditLocked
                                    ? { color: 'text.secondary', fontSize: '0.85rem' }
                                    : { cursor: 'pointer', color: '#1877F2', textDecoration: 'underline', fontSize: '0.85rem' }}
                                onClick={e => {
                                    if (isProductEditLocked) return;
                                    e.stopPropagation();
                                    editRow(params.row.index);
                                }}
                            >
                                {productCode}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                {description}
                            </Typography>
                        </Box>
                        {(isColVisible(COL.UNIT) || isColVisible(COL.TAX_CODE)) && (
                            <Typography variant="caption" display="block">
                                {isColVisible(COL.UNIT) && `Unit: ${unit || 'N/A'}`}
                                {isColVisible(COL.UNIT) && isColVisible(COL.TAX_CODE) && ' | '}
                                {isColVisible(COL.TAX_CODE) && `GST: ${tax}%`}
                            </Typography>
                        )}
                        {(isSameState ? (isColVisible(COL.SGST) || isColVisible(COL.CGST)) : isColVisible(COL.IGST)) && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                {isSameState
                                    ? [
                                        isColVisible(COL.SGST) && `SGST: ₹${formatINR(SGSTAmt)}`,
                                        isColVisible(COL.CGST) && `CGST: ₹${formatINR(CGSTAmt)}`,
                                    ].filter(Boolean).join(' + ')
                                    : `IGST: ₹${formatINR(IGSTAmt)}`}
                            </Typography>
                        )}
                        {((batchNo && isColVisible(COL.BATCH_NO)) || (expDate && isColVisible(COL.EXP_DATE)) || serialNo) && (
                            <Typography variant="caption" display="block">
                                {batchNo && isColVisible(COL.BATCH_NO) && `Batch: ${batchNo}`}
                                {expDate && isColVisible(COL.EXP_DATE) && ` | Exp: ${expDate}`}
                            </Typography>
                        )}
                        {remark && isColVisible(COL.REMARK) && (<Typography variant="caption" display="block" color="text.secondary"> Remark: {remark} </Typography>)}
                        {(BillNumber || BillDate) && (
                            <Typography variant="caption" display="block" color='blue'>
                                {BillNumber && `InvoiceNo: ${BillNumber}`}
                                {BillDate && ` | Date: ${BillDate}`}
                            </Typography>
                        )}
                    </Box>
                );
            }
        },
        { field: 'qty', headerName: 'Qty', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'rate', headerName: 'Rate', width: 100, align: 'right', headerAlign: 'center', renderCell: params => `₹${formatINR(params.row.rate)}` },
        { field: 'disc', headerName: 'Disc (%)', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'amount', headerName: 'Amount (₹)', width: 100, align: 'right', headerAlign: 'right', renderCell: params => `₹${formatINR(Number(params.row.TaxableAmt || 0))}` },
        {
            width: 30, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: params => (
                <IconButton size="small" onClick={() => deleteRow(params.row.index)} color="error">
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <Paper sx={{ p: 1, overflowX: 'hidden', mt: -1.5 }}>
            <Box display="flex" flexDirection={isSmall ? 'column' : 'row'} gap={2} sx={{ width: '100%', boxSizing: 'border-box', flexWrap: isSmall ? 'wrap' : 'nowrap', overflow: 'hidden' }} >
                <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%', maxWidth: isSmall ? '100%' : 420, boxSizing: 'border-box', p: 2, borderRight: isSmall ? 'none' : '1px solid #e0e0e0', flex: { lg: '1 1 100%' }, }} >
                    <Box>
                        <DropdownProductCode ref={productCodeRef} id="productCode" options={allProducts} value={current.productCode} onSelect={handleSelect} onNewProductAdded={loadAllProducts} connectionString={connectionString} searchBy="code" onProductMasterClose={handleProductAdded} autoFocus={!initialData?.tableData?.length} />
                        {!!errors.productCode && (<Typography color="error" variant="caption"> {errors.productCode} </Typography>)}
                    </Box>
                    {/* <TextField label="Description" required fullWidth multiline minRows={1} size="small" placeholder="Enter product description" value={current.description} onChange={handleChange('description')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiInputBase-root:not(.MuiInputBase-multiline)': { height: 40 }, '& .MuiOutlinedInput-inputMultiline': { padding: '8px 12px', minHeight: '80px' } }} /> */}
                    {isColVisible(COL.DESCRIPTION) && (
                        <DropdownProductCode
                            id="description"
                            label="Description"
                            options={allProducts}
                            value={current.description}
                            onSelect={handleSelect}
                            onNewProductAdded={loadAllProducts}
                            connectionString={connectionString}
                            searchBy="description"   // âœ… IMPORTANT
                            onProductMasterClose={handleProductAdded}
                        />
                    )}
                    {(isColVisible(COL.PO_NO) || isColVisible(COL.PO_DATE) || isColVisible(COL.PO_QTY)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.PO_NO) && (
                                <TextField label={getColLabel(COL.PO_NO, 'PO No')} size="small" fullWidth value={current.refNo} onChange={handleChange('PONO')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PO_DATE) && (
                                <TextField label={getColLabel(COL.PO_DATE, 'PO Date')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.refDate} onChange={handleChange('PODate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PO_QTY) && (
                                <TextField label={getColLabel(COL.PO_QTY, 'PO Qty')} type="number" size="small" fullWidth value={current.refQty} onChange={handleChange('POQty')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.TYPE) || isColVisible(COL.SIZE) || isColVisible(COL.COLOR)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.TYPE) && (
                                <TextField label={getColLabel(COL.TYPE, 'Type')} size="small" fullWidth value={current.type} onChange={handleChange('type')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.SIZE) && (
                                <TextField label={getColLabel(COL.SIZE, 'Size')} size="small" fullWidth value={current.size} onChange={handleChange('size')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.COLOR) && (
                                <TextField label={getColLabel(COL.COLOR, 'Color')} size="small" fullWidth value={current.color} onChange={handleChange('color')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    <Box display="flex" flexDirection="row" gap={1.5}>
                        {isColVisible(COL.UNIT) && (
                            <TextField label="Unit" select required fullWidth size='small' value={String(current.UnitId)} onChange={handleChange('UnitId')} disabled={mode === 'ref'} SelectProps={{ native: true }} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} >
                                <option value=""></option>
                                {unitOptions.map(u => (
                                    <option key={u.UnitId} value={u.UnitId}>
                                        {u.unit}
                                    </option>
                                ))}
                            </TextField>
                        )}
                        {/* <TextField label="Tax(%)" fullWidth type="number" disabled value={current.tax} onChange={handleChange('tax')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} /> */}

                        {isColVisible(COL.TAX_CODE) && (
                            <Dropdown
                                id="gst"
                                label="GST"
                                valueKey="Id"
                                labelKey="value"
                                value={current.TaxId ? String(current.TaxId) : ""}
                                options={gsts}
                                required
                                disabled={mode === 'ref'}
                                onChange={(opt) => {
                                    if (!opt) {
                                        setCurrent(prev => ({
                                            ...prev,
                                            tax: 0,
                                            TaxId: null
                                        }));
                                    } else {
                                        const gstValue = parseFloat(opt.value) || 0;

                                        setCurrent(prev => ({
                                            ...prev,
                                            tax: gstValue,   // % value used in calculation
                                            TaxId: opt.Id    // backend id
                                        }));
                                    }
                                }}

                                 error={!!errors.TaxId}
                            />
                        )}
                    </Box>
                    {(isColVisible(COL.BATCH_NO) || isColVisible(COL.EXP_DATE)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.BATCH_NO) && (
                                <TextField label={getColLabel(COL.BATCH_NO, 'Batch No')} size="small" fullWidth value={current.batchNo} onChange={handleChange('batchNo')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.EXP_DATE) && (
                                <TextField label={getColLabel(COL.EXP_DATE, 'Exp. Date')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.expDate} onChange={handleChange('expDate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {isApplyMRPInSaleEnabled ? (
                        // When Apply MRP in Sale is enabled: Qty, MRP, Rate, Disc in single row
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {isColVisible(COL.QTY) && (
                                <Box sx={{ flex: 0.75, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <TextField
                                        label="Qty"
                                        required
                                        size='small'
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, step: 'any' }}
                                        value={current.qty}
                                        onChange={handleChange('qty')}
                                        error={!!errors.qty}
                                        helperText={errors.qty}
                                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                    />
                                </Box>
                            )}
                            {/* MRP Field - visible when Apply MRP is enabled */}
                            <Box sx={{ flex: 1, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                <TextField
                                    label="MRP"
                                    size='small'
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    value={current.MRP}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const mrpValue = Number(val) || 0;

                                        setCurrent(prev => ({
                                            ...prev,
                                            MRP: val === '' ? '' : mrpValue
                                        }));

                                        if (mrpValue > 0 && current.tax > 0) {
                                            const newRate = calculateSaleRateFromMRP(mrpValue, current.tax);
                                            const taxableAmt = current.qty * newRate * (1 - current.disc / 100);
                                            const taxAmount = taxableAmt * (current.tax / 100);

                                            setCurrent(prev => ({
                                                ...prev,
                                                rate: newRate,
                                                amount: taxableAmt.toFixed(2),
                                                taxAmount: taxAmount.toFixed(2),
                                                CGST: state?.toLowerCase() === compState?.toLowerCase() ? (current.tax / 2).toFixed(2) : '0.00',
                                                SGST: state?.toLowerCase() === compState?.toLowerCase() ? (current.tax / 2).toFixed(2) : '0.00',
                                                IGST: state?.toLowerCase() !== compState?.toLowerCase() ? current.tax.toFixed(2) : '0.00',
                                                CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
                                                SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
                                                IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
                                                TaxableAmt: taxableAmt.toFixed(2)
                                            }));
                                        }
                                    }}
                                    InputLabelProps={{ sx: requiredAsteriskStyle }}
                                    sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                />
                            </Box>
                            {isColVisible(COL.RATE) && (
                                <Box sx={{ flex: 1, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <TextField
                                        label="Rate"
                                        required
                                        size='small'
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        value={current.rate}
                                        onChange={handleChange('rate')}
                                        disabled={true}
                                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                    />
                                </Box>
                            )}
                            {isColVisible(COL.DISC) && (
                                <Box sx={{ flex: 0.75, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <TextField
                                        label={getColLabel(COL.DISC, 'Disc%')}
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, max: 100 }}
                                        value={current.disc}
                                        onChange={handleChange('disc')}
                                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        // When Apply MRP is not enabled: Qty, Rate, Disc in separate columns (original layout)
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.QTY) && (
                                <TextField label="Qty" required size='small' fullWidth type="number" inputProps={{ min: 0, step: 'any' }} value={current.qty} onChange={handleChange('qty')} error={!!errors.qty} helperText={errors.qty} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.RATE) && (
                                    <TextField label="Rate" required size='small' fullWidth type="number" inputProps={{ min: 0 }} value={current.rate} onChange={handleChange('rate')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.DISC) && (
                                <TextField label={getColLabel(COL.DISC, 'Disc%')} size='small' fullWidth type="number" inputProps={{ min: 0, max: 100 }} value={current.disc} onChange={handleChange('disc')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.PKG) || isColVisible(COL.FREE_QTY) || isColVisible(COL.PER)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.PKG) && (
                                <TextField label={getColLabel(COL.PKG, 'Pkg')} size="small" fullWidth value={current.pkg || ''} onChange={handleChange('pkg')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.FREE_QTY) && (
                                <TextField label={getColLabel(COL.FREE_QTY, 'Free Qty')} type="number" size="small" fullWidth value={current.freeQty} onChange={handleChange('freeQty')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PER) && (
                                <TextField label={getColLabel(COL.PER, 'Per')} size="small" fullWidth value={current.per} onChange={handleChange('per')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.OTH1) || isColVisible(COL.OTH2) || isColVisible(COL.OTH3)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.OTH1) && (
                                <TextField label={getColLabel(COL.OTH1, 'Oth 1')} size="small" fullWidth value={current.oth1} onChange={handleChange('oth1')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH2) && (
                                <TextField label={getColLabel(COL.OTH2, 'Oth 2')} size="small" fullWidth value={current.oth2} onChange={handleChange('oth2')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH3) && (
                                <TextField label={getColLabel(COL.OTH3, 'Oth 3')} size="small" fullWidth value={current.oth3} onChange={handleChange('oth3')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.UTGST) || isColVisible(COL.GST_CESS) || isColVisible(COL.GST_OTH)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.UTGST) && (
                                <TextField label={getColLabel(COL.UTGST, 'UTGST')} size="small" fullWidth type="number" value={current.UTGST} onChange={handleChange('UTGST')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.GST_CESS) && (
                                <TextField label={getColLabel(COL.GST_CESS, 'GST Cess')} size="small" fullWidth type="number" value={current.GSTCess} onChange={handleChange('GSTCess')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.GST_OTH) && (
                                <TextField label={getColLabel(COL.GST_OTH, 'GST Oth')} size="small" fullWidth type="number" value={current.GSTOth} onChange={handleChange('GSTOth')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {isColVisible(COL.AMOUNT) && (
                        <TextField label="Amount" fullWidth disabled type="number" size="small" value={current.amount} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                    )}
                    {isColVisible(COL.REMARK) && (
                        <Box>
                            <TextField label="Remark" fullWidth multiline minRows={1} maxRows={3} size="small" value={current.remark || ''} onChange={handleChange('remark')} inputProps={{ maxLength: 250 }} helperText={`${(current.remark || '').length}/250 characters`} />
                        </Box>
                    )}
                    <Box display="flex" justifyContent="center">
                        <Button variant="outlined" onClick={addProduct} sx={{ minWidth: 180 }}>
                            {editIndex != null ? 'Update Product' : 'Add Product'}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ width: isSmall ? '100%' : 670, maxWidth: '100%', p: isSmall ? 0 : 0, pl: 2, marginTop: isMobile ? -1 : -4, flexShrink: 0 }}>
                    {isMobile ? (
                        <Box>
                            {rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).map((row, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        border: "1px solid #cbd5e1",
                                        borderRadius: 2,
                                        p: 1.5,
                                        mb: 1.5,
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {row.productCode} â€”{" "}
                                        <Box
                                            component="span"
                                            sx={{
                                                fontStyle: "italic",
                                                fontWeight: "normal",
                                                display: "inline",
                                                wordBreak: "break-word",
                                                whiteSpace: "normal"
                                            }}
                                        >
                                            {row.description}
                                        </Box>
                                    </Typography>
                                    {(row.BillNumber || row.BillDate) && (
                                        <Typography variant="caption" display="block" color='blue'>
                                            {row.BillNumber && `InvoiceNo: ${row.BillNumber}`}
                                            {row.BillDate && ` | Date: ${row.BillDate}`}
                                        </Typography>
                                    )}
                                    <Grid container justifyContent="space-between">
                                        <Typography>Qty:</Typography>
                                        <Typography>{row.qty}</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Rate:</Typography>
                                        <Typography>₹{row.rate}</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Disc:</Typography>
                                        <Typography>{row.disc}%</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Amount:</Typography>
                                        <Typography>₹{row.amount}</Typography>
                                    </Grid>

                                    {/* TAX INFO */}
                                    <Grid container justifyContent="space-between">
                                        <Typography>GST:</Typography>
                                        <Typography>{row.tax}%</Typography>
                                    </Grid>

                                    {state?.toLowerCase() === compState?.toLowerCase() ? (
                                        <>
                                            <Grid container justifyContent="space-between">
                                                <Typography>CGST:</Typography>
                                                <Typography>₹{formatINR(row.CGSTAmt)}</Typography>
                                            </Grid>
                                            <Grid container justifyContent="space-between">
                                                <Typography>SGST:</Typography>
                                                <Typography>₹{formatINR(row.SGSTAmt)}</Typography>
                                            </Grid>
                                        </>
                                    ) : (
                                        <Grid container justifyContent="space-between">
                                            <Typography>IGST:</Typography>
                                            <Typography>₹{formatINR(row.IGSTAmt)}</Typography>
                                        </Grid>
                                    )}

                                    {/* Remark */}
                                    {row.remark && row.remark.trim() !== '' && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                mt: 1,
                                                display: 'block',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                whiteSpace: 'normal'
                                            }}
                                        >
                                            Remark: {row.remark}
                                        </Typography>
                                    )}

                                    {/* DELETE / EDIT BUTTONS IF NEEDED */}
                                    <Box display="flex" justifyContent="flex-end" mt={1} gap={0.5}>
                                        <IconButton size="small" onClick={() => editRow(i)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => deleteRow(i)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <DataGrid
                            rows={rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).map((r, i) => ({ ...r, id: i + 1, index: i }))}
                            columns={columns}
                            getRowHeight={() => 'auto'}
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooter
                            sx={{
                                mt: 5,
                                border: '1px solid #cbd5e1',
                                borderRadius: 1,
                                '& .MuiDataGrid-columnHeaders': { minHeight: 55, maxHeight: 55 },
                                '& .MuiDataGrid-columnHeader': { backgroundColor: '#f0f0f0', paddingTop: '4px', paddingBottom: '4px', height: 42, lineHeight: '1.2rem', fontSize: '13px' },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#edf2feff' },
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#ffffff' }
                            }}
                        />
                    )}
                    <Box mt={3}>
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Basic Amount</Typography>
                            <Typography>₹{formatINR(totals.basic)}</Typography>
                        </Grid>
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Discounted Amount</Typography>
                            <Typography>₹{formatINR(totals.discount)}</Typography>
                        </Grid>

                        {additionalCharges.length > 0 && (
                            <Box mb={2}>
                                <Accordion
                                    expanded={chargesCollapsibleOpen !== false ? true : chargesCollapsibleOpen}
                                    onChange={() => setChargesCollapsibleOpen(!chargesCollapsibleOpen)}
                                    sx={{ width: '100%' }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75, minHeight: 'auto' }}>
                                        <Typography variant="subtitle2">Charges</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ py: 1, px: 1 }}>
                                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <colgroup>
                                                    <col style={{ width: '45%' }} />
                                                    <col style={{ width: '18%' }} />
                                                    <col style={{ width: '18%' }} />
                                                    <col style={{ width: '19%' }} />
                                                </colgroup>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: '1px solid #ddd', fontWeight: 600, fontSize: '12px' }}>Charge Name</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: '1px solid #ddd', fontWeight: 600, fontSize: '12px' }}>Amount</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: '1px solid #ddd', fontWeight: 600, fontSize: '12px' }}>Tax</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, fontSize: '12px' }}>Final Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {additionalCharges.map(charge => {
                                                        const key = charge.TransType;
                                                        if (!chargeInputs[key]) {
                                                            updateChargeInput(key, { amount: '', taxId: charge.TaxId ?? null });
                                                        }
                                                        const input = chargeInputs[key] || { amount: '', taxId: charge.TaxId ?? null };
                                                        const { finalAmount } = getChargeFinalAmounts(charge, input);

                                                        return (
                                                            <tr key={key} style={{ borderBottom: '1px solid #ddd' }}>
                                                                <td style={{ padding: '6px 8px', borderRight: '1px solid #ddd', verticalAlign: 'middle' }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '12px' }}>{charge.ChargeName}</Typography>
                                                                </td>
                                                                <td style={{ padding: '6px 8px', borderRight: '1px solid #ddd', verticalAlign: 'middle' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        type="number"
                                                                        value={input.amount}
                                                                        onChange={(e) => updateChargeInput(key, { amount: e.target.value })}
                                                                        sx={{ '& .MuiOutlinedInput-root': { height: 30, fontSize: '12px' } }}
                                                                    />
                                                                </td>
                                                                <td style={{ padding: '6px 8px', borderRight: '1px solid #ddd', verticalAlign: 'middle' }}>
                                                                    {charge.TaxEnabled ? (
                                                                        <Dropdown
                                                                            id={`charge-tax-${key}`}
                                                                            valueKey="Id"
                                                                            labelKey="value"
                                                                            value={input.taxId ? String(input.taxId) : ""}
                                                                            options={gsts}
                                                                            onChange={(opt) => updateChargeInput(key, { taxId: opt ? opt.Id : null })}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" sx={{ color: '#999', fontSize: '12px' }}>N/A</Typography>
                                                                    )}
                                                                </td>
                                                                <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        type="number"
                                                                        value={finalAmount.toFixed(2)}
                                                                        disabled
                                                                        sx={{ '& .MuiOutlinedInput-root': { height: 30, backgroundColor: '#f5f5f5', fontSize: '12px' } }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}

                        {state?.toLowerCase() === compState?.toLowerCase() ? (
                            <>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>CGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.cgst) + chargesSummary.cgst)}</Typography>
                                </Grid>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>SGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.sgst) + chargesSummary.sgst)}</Typography>
                                </Grid>
                            </>
                        ) : (
                            totals.igst > 0 && (
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>IGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.igst) + chargesSummary.igst)}</Typography>
                                </Grid>
                            )
                        )}
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Round Off ({Number(totals.roundOff) >= 0 ? '+' : '-'})</Typography>
                            <Typography>₹{formatINR(totals.roundOff)}</Typography>
                        </Grid>

                        <Divider sx={{ my: 1 }} />
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600}>Total Amount</Typography>
                            <Typography fontWeight={600}>₹{formatINR(Number(totals.total))}</Typography>
                        </Grid>
                    </Box>
                </Box>
            </Box>
            {!!errors.global && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {errors.global}
                </Typography>
            )}

            {isRecordFromReference && adjustAdvancePayment ? (
                <Box mb={2} mt={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Advance Payment: ₹{formatINR(adjustAmt)}
                    </Typography>
                </Box>
            ) : null}

            <Box mt={1} display="flex" justifyContent="flex-end">
                <Button variant="contained" onClick={handleNext} disabled={editIndex !== null || isProductEditLocked}>
                    Next
                </Button>
            </Box>

            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={stockWarningOpen}
                onClose={() => {
                    setStockWarningOpen(false);
                    if (stockConfirmResolve) stockConfirmResolve(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: '#d32f2f', color: 'white', fontWeight: 'bold' }}>
                    ⚠️ Stock Warning
                </DialogTitle>
                <DialogContent sx={{ mt: 2, mb: 1 }}>
                    <p style={{ color: '#d32f2f', fontWeight: 500 }}>
                        {stockWarningData?.message || 'Stock will exceed maximum level. Do you want to save?'}
                    </p>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setStockWarningOpen(false);
                            if (stockConfirmResolve) stockConfirmResolve(false);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setStockWarningOpen(false);
                            if (stockConfirmResolve) stockConfirmResolve(true);
                        }}
                        sx={{ backgroundColor: '#d32f2f', textTransform: 'none', '&:hover': { backgroundColor: '#b71c1c' } }}
                    >
                        Yes, Save Anyway
                    </Button>
                </DialogActions>
            </Dialog>

            {/* "Enable Convesrion effect in Transaction?" — Conversion Qty popup, shown when Unit is
                changed away from the product's default Unit. */}
            <Dialog
                open={conversionQtyDialogOpen}
                onClose={handleCancelConversionQty}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                    Unit Conversion
                </DialogTitle>
                <DialogContent sx={{ py: 3 }}>
                    <Typography sx={{ fontSize: '14px', color: '#555', mb: 2 }}>
                        How many {pendingUnitChange?.unit || 'unit'} make 1 {current.unit || 'base unit'}?
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Conversion Qty"
                        size="small"
                        type="text"
                        value={conversionQtyInput}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d*\.?\d{0,4}$/.test(val)) setConversionQtyInput(val);
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancelConversionQty}
                        sx={{ color: '#666', borderColor: '#ddd' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmConversionQty}
                        sx={{ bgcolor: '#1877F2', '&:hover': { bgcolor: '#0d5dc7' } }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

        </Paper>
    );
};

export default ProductDetails;
